/**
 * Syncing from the page: runs syncCore while the app is open, and asks the
 * service worker to take over (Background Sync) when the device has no signal,
 * so changes go up once it returns even if the app has been closed.
 *
 * - Runs one at a time; the shared Web Lock also keeps it from overlapping
 *   with the service worker.
 * - A lost connection just stops the run; the outbox is untouched and the next
 *   run (here or in the service worker) resends. Re-sent operations are
 *   answered by the server's receipts, so nothing is applied twice.
 *
 * Triggered after every change (debounced), when the device comes back
 * online, when the app returns to the foreground, every minute while changes
 * are waiting, and from the "Sync now" button. Status is broadcast as
 * 'sync:status' events (SyncContext listens).
 */
import { api, cachedUser, get as networkGet, isNetworkError } from './http';
import { countOutbox, dismissProblems, readMeta, readProblems } from './offlineStore';
import { lastSyncKey, syncOutbox, withSyncLock } from './syncCore';

export const BACKGROUND_SYNC_TAG = 'mangrove-outbox';
const SYNC_TIMEOUT = 120000;

const state = { syncing: false, error: null };
let running = null;
let timer = null;

/** Broadcast the current status: { syncing, error, pending, problems, lastSyncedAt }. */
export const announceSyncStatus = async () => {
  const user = cachedUser.get();
  const detail = { ...state, pending: 0, problems: [], lastSyncedAt: null };
  if (user?.id) {
    const [pending, problems, lastSyncedAt] = await Promise.all([
      countOutbox(user.id),
      readProblems(user.id),
      readMeta(lastSyncKey(user.id)),
    ]);
    Object.assign(detail, { pending, problems, lastSyncedAt: lastSyncedAt || null });
  }
  window.dispatchEvent(new CustomEvent('sync:status', { detail }));
  return detail;
};

// Ask the browser to wake the service worker when there's signal again, even
// if the app is closed by then (Chrome / Edge; elsewhere this is a no-op).
const requestBackgroundSync = async () => {
  try {
    const registration = await navigator.serviceWorker?.ready;
    await registration?.sync?.register(BACKGROUND_SYNC_TAG);
  } catch {
    // Not supported or not allowed: the app syncs next time it's open.
  }
};

const run = async () => {
  const user = cachedUser.get();
  if (!user?.id) return;
  if (!(await countOutbox(user.id))) {
    await announceSyncStatus();
    return;
  }
  if (navigator.onLine === false) {
    requestBackgroundSync();
    await announceSyncStatus();
    return;
  }
  state.syncing = true;
  state.error = null;
  await announceSyncStatus();
  try {
    await withSyncLock(() =>
      syncOutbox({
        userId: user.id,
        postSync: (operations) =>
          api.post('/sync', { operations }, { timeout: SYNC_TIMEOUT }).then((r) => r.data?.data?.results || []),
        fetchTree: () => networkGet('/surveys'),
        onChunk: announceSyncStatus,
      })
    );
    // Pages showing field data reload (useSyncVersion).
    window.dispatchEvent(new Event('records:synced'));
  } catch (error) {
    if (isNetworkError(error)) {
      state.error = 'offline';
      requestBackgroundSync();
    } else {
      state.error = error.response?.data?.message || 'Sync failed. Your changes are still saved on this device.';
    }
  } finally {
    state.syncing = false;
    await announceSyncStatus();
  }
};

/** Sync now (or join the run already in progress). */
export const syncNow = () => {
  clearTimeout(timer);
  if (!running) running = run().finally(() => { running = null; });
  return running;
};

/** Sync shortly, so a burst of quick changes goes in one request. */
export const scheduleSync = (delay = 600) => {
  clearTimeout(timer);
  timer = setTimeout(syncNow, delay);
  announceSyncStatus();
};

export const dismissSyncProblems = async (ids) => {
  await dismissProblems(ids);
  return announceSyncStatus();
};

let started = false;

/** Install the automatic triggers (once per page). */
export const startAutoSync = () => {
  if (started) return;
  started = true;
  window.addEventListener('online', () => syncNow());
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible') {
      syncNow();
      return;
    }
    // Being hidden or closed: the page may not get to finish, so hand any
    // waiting changes to the service worker, which keeps running briefly.
    const user = cachedUser.get();
    if (user?.id && (await countOutbox(user.id))) {
      navigator.serviceWorker?.controller?.postMessage({ type: 'sync-outbox' });
    }
  });
  setInterval(async () => {
    const user = cachedUser.get();
    if (user?.id && (await countOutbox(user.id))) syncNow();
  }, 60000);
  // The service worker synced in the background: refresh what's on screen.
  navigator.serviceWorker?.addEventListener('message', (event) => {
    if (event.data?.type === 'outbox-synced') {
      announceSyncStatus();
      window.dispatchEvent(new Event('records:synced'));
    }
  });
};
